import AiLabel from "@/components/ai-label";
import Category from "@/components/category";
import LinkedContent from "@/components/linked-content";
import TeaserByLineImage from "@/components/teaser-byline-image";
import TeaserDownpull from "@/components/teaser-downpull";
import TeaserHeading from "@/components/teaser-heading";
import Vinjett from "@/components/vinjett";
import { Content } from "@/services/content/types";
import { createImageSizes, createImageUrl } from "@/utils/image";
import { wrapPrefix } from "@/utils/teaser";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";

type MediumProps = {
  specialByline?: React.ReactNode;
  article: Content.Article;
  hideImage?: boolean;
  hideCategory?: boolean;
  showLinkedContent?: boolean;
  hideDownPull?: boolean;
};

const Medium = (props: MediumProps) => {
  const hasLinkedContent = Boolean(props.article.fields.linkedContent?.length);
  const category = props.article.fields.categories?.[0];
  const image =
    props.article.fields.teaserImage || props.article.fields.images?.[0];
  const title = props.article.fields.teaserTitle || props.article.fields.title;
  const author = props.article.fields.authors?.[0];
  const vinjett = props.article.fields.vinjett;
  const highlighted = props?.article?.fields?.highlighted_v2;
  const parentCategory = category?.fields.parentCategory;
  const color = category?.fields.color || parentCategory?.fields.color;
  const excerpt = props.article.fields.excerpt
    ? wrapPrefix(/^(Liverapport:?)/i, props.article.fields.excerpt, "font-bold")
    : null;
  const showByline =
    props.article.fields.showByline !== undefined
      ? props.article.fields.showByline
      : false;
  const bylineImage =
    props.article.fields.hideBylineImage !== undefined
      ? props.article.fields.hideBylineImage
      : false;
  const teaserLayoutType = props.article.fields.teaserLayoutType;

  // Specialbylinen ska ligga sist i paketet, efter eventuellt kopplat innehåll.
  const showsLinkedContent =
    hasLinkedContent && props.showLinkedContent !== false;
  const specialBylineFooter = props.specialByline && (
    <div className="pb-4 border-b border-sand-800">{props.specialByline}</div>
  );

  return (
    <>
      <LinkedContent
        linkedContent={props.article.fields.linkedContent}
        color={color}
        highlighted={highlighted}
        showLinkedContent={props.showLinkedContent}
        compactMode={true}
        teaserLayoutType={teaserLayoutType}
        footer={specialBylineFooter}
      >
        <Link
          href={`/${props.article.fields.slug}`}
          className={classNames(
            "group block relative pb-4 border-b border-sand-800",
            {
              "!border-b-0 !pb-0": !!props.specialByline && !showsLinkedContent,
              "bg-red-900 !border-red-950 text-static-white":
                highlighted === "Nivå 1(röd)",
              "": highlighted === "Nivå 2(beige)",
              "": bylineImage,
              "": teaserLayoutType === "Centrerad rubrik" && hasLinkedContent,
            },
          )}
        >
          {vinjett && <Vinjett content={vinjett} size="medium" />}

          {teaserLayoutType == "Rubrik på bild" &&
          image?.fields?.file?.url &&
          !props.hideImage ? (
            <div className="relative grid">
              <div className="[grid-area:1/1] self-start justify-self-stretch aspect-w-16 aspect-h-9">
                <Image
                  fill
                  src={createImageUrl(image.fields.file.url, { crop: "16:9" })}
                  alt={image.fields.alt || ""}
                  className="object-cover"
                  sizes={createImageSizes({
                    mobile: "100vw",
                    tablet: "50vw",
                    desktop: "33vw",
                  })}
                />
                <AiLabel image={image} />
              </div>
              <div className="relative [grid-area:1/1] self-end justify-self-stretch p-4 pb-4 bg-gradient-to-t from-black/90 to-transparent text-static-white">
                {category && !props.hideCategory && (
                  <Category category={category} />
                )}
                {title && (
                  <TeaserHeading
                    text={title}
                    highlighted={highlighted}
                    className={classNames(
                      "text-xl md:text-2xl font-semibold text-static-white",
                      !!highlighted && "!text-3xl",
                    )}
                  />
                )}
                {!props.hideDownPull && excerpt && (
                  <span className="block mt-2 text-sm md:text-base text-static-white">
                    {showByline && (
                      <span className="font-bold">{author?.fields.name}: </span>
                    )}
                    <span>{props.article.fields.excerpt}</span>
                  </span>
                )}
              </div>
            </div>
          ) : (
            <>
              {image?.fields?.file?.url && !props.hideImage && (
                <div className="aspect-w-16 aspect-h-9 mb-4">
                  <Image
                    fill
                    src={createImageUrl(image.fields.file.url, {
                      crop: "16:9",
                    })}
                    alt={image.fields.alt || ""}
                    className="object-cover"
                    sizes={createImageSizes({
                      mobile: "100vw",
                      tablet: "50vw",
                      desktop: "33vw",
                    })}
                  />
                  <AiLabel image={image} />
                </div>
              )}
              <div
                className={classNames({
                  "px-4": !!highlighted && !hasLinkedContent,
                })}
              >
                {category && !props.hideCategory && (
                  <Category
                    category={category}
                    centered={teaserLayoutType === "Centrerad rubrik"}
                  />
                )}

                {title && (
                  <TeaserHeading
                    text={title}
                    highlighted={highlighted}
                    className={classNames(
                      "text-xl md:text-2xl font-semibold",
                      bylineImage ? "max-w-[73%] lg:max-w-[79%]" : "",
                      !!highlighted && "!text-3xl",
                      teaserLayoutType === "Centrerad rubrik"
                        ? "text-center"
                        : "",
                    )}
                  />
                )}
                {!props.hideDownPull && (
                  <div className={classNames(bylineImage ? "max-w-[70%]" : "")}>
                    <TeaserDownpull
                      highlighted={highlighted}
                      showByline={showByline}
                      authorName={author?.fields.name}
                      excerpt={props.article.fields.excerpt}
                      showbyLineImage={bylineImage}
                      centered={teaserLayoutType === "Centrerad rubrik"}
                    />
                  </div>
                )}
              </div>

              {bylineImage && author?.fields.image?.fields?.file?.url && (
                <TeaserByLineImage
                  showbyLineImage={bylineImage}
                  fileUrl={author?.fields.image?.fields?.file?.url}
                  altText=""
                  width={"190px"}
                  height={"190px"}
                  wrapperClassNames={`max-w-[30%] lg:max-w-[25%] ${
                    (!hasLinkedContent && bylineImage) || bylineImage ? "" : ""
                  }`}
                />
              )}
            </>
          )}
        </Link>
      </LinkedContent>
    </>
  );
};

export default Medium;
